import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Warehouse as WarehouseIcon } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const normalizeName = (value) => (value || '').toLowerCase();

const isSalesWarehouse = (warehouse) => (warehouse?.warehouse_type || 'SALES') === 'SALES';
const normalizeTag = (value) => (value || '').toUpperCase();

export default function WarehouseOverviewPage() {
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [rawMain, setRawMain] = useState([]);
  const [rawPacking, setRawPacking] = useState([]);
  const [salesStock, setSalesStock] = useState([]);

  useEffect(() => {
    fetchOverview();
  }, []);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const warehousesRes = await adminApi.getWarehouses();
      const list = warehousesRes.data || [];
      setWarehouses(list);

      const activeSales = list.filter((w) => w?.is_active !== false && isSalesWarehouse(w));
      const defaultSalesWarehouse = activeSales.find((w) => w.is_default_sales);
      const taggedMainWarehouse = activeSales.find((w) => normalizeTag(w.tag) === 'MAIN');
      const taggedPackingWarehouse = activeSales.find((w) => normalizeTag(w.tag) === 'PACKING');
      const mainWarehouse =
        taggedMainWarehouse ||
        list.find((w) => normalizeName(w.name).includes('main')) ||
        defaultSalesWarehouse ||
        activeSales[0];
      const packingWarehouse =
        taggedPackingWarehouse ||
        list.find((w) => normalizeName(w.name).includes('packing') || normalizeName(w.code).includes('packing')) ||
        activeSales.find((w) => !w.is_default_sales) ||
        activeSales[0];
      const salesWarehouse = list.find((w) => w.is_default_sales) || list.find((w) => normalizeName(w.name).includes('sales'));

      const [mainRawRes, packingRawRes, salesStockRes] = await Promise.all([
        mainWarehouse ? adminApi.getRawStockLots({ warehouse_id: mainWarehouse.id }) : Promise.resolve({ data: [] }),
        packingWarehouse ? adminApi.getRawStockLots({ warehouse_id: packingWarehouse.id }) : Promise.resolve({ data: [] }),
        salesWarehouse ? adminApi.getWarehouseStock(salesWarehouse.id) : Promise.resolve({ data: [] }),
      ]);

      setRawMain(mainRawRes.data || []);
      setRawPacking(packingRawRes.data || []);
      setSalesStock(salesStockRes.data || []);
    } catch (error) {
      toast.error('Failed to load warehouse overview');
    } finally {
      setLoading(false);
    }
  };

  const aggregateRaw = (lots) => {
    const map = new Map();
    (lots || []).forEach((lot) => {
      const kg = Number(lot.kg_total || 0) || 0;
      if (kg <= 0) return;
      const key = lot.raw_item_name || 'Unknown';
      const total = map.get(key) || 0;
      map.set(key, total + kg);
    });
    return Array.from(map.entries()).map(([name, totalKg]) => ({ name, totalKg }));
  };

  const mainRawCards = useMemo(() => aggregateRaw(rawMain), [rawMain]);
  const packingRawCards = useMemo(() => aggregateRaw(rawPacking), [rawPacking]);

  const salesRows = useMemo(() => {
    return [...(salesStock || [])]
      .map((row) => ({
        id: row.variant_id,
        name: row.master_name,
        pack: row.pack_size_label,
        available: Number(row.available || 0),
      }))
      .sort((a, b) => `${a.name} ${a.pack}`.localeCompare(`${b.name} ${b.pack}`));
  }, [salesStock]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#C97A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="warehouse-overview-page">
      <div>
        <h1 className="text-3xl font-bold text-[#2D2420]">Warehouse Overview</h1>
        <p className="text-[#A8A8A8] mt-1">Stock summary for raw and sale-ready items</p>
      </div>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Main Warehouse — Raw Stock</CardTitle>
        </CardHeader>
        <CardContent>
          {mainRawCards.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              <WarehouseIcon className="w-5 h-5 mr-2" /> No raw stock
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mainRawCards.map((item) => (
                <Card key={item.name} className="bg-[#FAFAF9] border-[#E5E5E5]">
                  <CardContent className="p-4">
                    <p className="text-sm text-[#A8A8A8]">{item.name}</p>
                    <p className="text-2xl font-semibold text-[#2D2420]">{item.totalKg.toFixed(2)} kg</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Packing Warehouse — Raw Stock</CardTitle>
        </CardHeader>
        <CardContent>
          {packingRawCards.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              <WarehouseIcon className="w-5 h-5 mr-2" /> No raw stock
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {packingRawCards.map((item) => (
                <Card key={item.name} className="bg-[#FAFAF9] border-[#E5E5E5]">
                  <CardContent className="p-4">
                    <p className="text-sm text-[#A8A8A8]">{item.name}</p>
                    <p className="text-2xl font-semibold text-[#2D2420]">{item.totalKg.toFixed(2)} kg</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-white border-[#E5E5E5]">
        <CardHeader>
          <CardTitle className="text-lg text-[#2D2420]">Sales Warehouse — Sale Ready Stock</CardTitle>
        </CardHeader>
        <CardContent>
          {salesRows.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-[#A8A8A8]">
              <WarehouseIcon className="w-5 h-5 mr-2" /> No stock
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAF9] border-b border-[#E5E5E5]">
                  <tr>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Product</th>
                    <th className="text-left py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Pack</th>
                    <th className="text-right py-3 px-4 text-xs font-semibold text-[#5C4033] uppercase">Available</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E5E5]">
                  {salesRows.map((row) => (
                    <tr key={row.id} className="hover:bg-[#FAFAF9]/50">
                      <td className="py-3 px-4 text-[#2D2420]">{row.name}</td>
                      <td className="py-3 px-4 text-[#A8A8A8]">{row.pack || '-'}</td>
                      <td className={cn('py-3 px-4 text-right font-semibold', row.available < 0 ? 'text-red-600' : 'text-[#2D2420]')}>
                        {Math.round(row.available)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
